import axios from 'axios';
import { BatchSpanProcessor, ConsoleSpanExporter, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SpanStatusCode, propagation, context } from '@opentelemetry/api';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_URL_PATH,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_HTTP_REQUEST_METHOD_ORIGINAL,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_USER_AGENT_ORIGINAL,
} from '@opentelemetry/semantic-conventions';
import { W3CTraceContextPropagator } from '@opentelemetry/core';


const exporter = new ConsoleSpanExporter(); // Temporary exporter for debugging
const processor = new BatchSpanProcessor(exporter);
const resource = defaultResource().merge(
  resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'WLP FE',
    [ATTR_SERVICE_VERSION]: process.env.REACT_APP_VERSION,
  })
);
const telemetryProvider = new WebTracerProvider({
  resource,
  spanProcessors: [processor]
});
telemetryProvider.register({
  propagator: new W3CTraceContextPropagator()
})
const tracer = telemetryProvider.getTracer('WLP FE', process.env.REACT_APP_VERSION);
if (process.env.NODE_ENV === 'production') {
  telemetryProvider.shutdown();
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      delete axios.defaults.headers.common['Authorization'];
    }
    return Promise.reject(error);
  }
)

const withTelemetry = async (config) => {
  if (!config.headers) {
    config.headers = {};
  }
  return tracer.startActiveSpan(
    config.url,
    async (span) => {
      try {
        propagation.inject(context.active(), config.headers);
        span.setAttribute(ATTR_HTTP_REQUEST_METHOD_ORIGINAL, config.method.toUpperCase());
        span.setAttribute(ATTR_URL_PATH, config.url);
        span.setAttribute(ATTR_USER_AGENT_ORIGINAL, navigator.userAgent);
        const response = await axios(config);
        span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, response.status);
        span.setStatus({ code: SpanStatusCode.OK });
        return response.data;
      } catch (err) {
        span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, err.response?.status);
        span.setStatus({ code: SpanStatusCode.ERROR, message: err.response?.data });
        err.traceId = span.spanContext().traceId;
        throw err;
      } finally {
        span.end();
      }
    }
  )

};

const http = {
  get: (url, options) => withTelemetry({ method: 'get', url, ...options }),
  post: (url, data, options) =>
    withTelemetry({ method: 'post', url, data, ...options }),
  put: (url, data, options) =>
    withTelemetry({ method: 'put', url, data, ...options }),
  patch: (url, data, options) =>
    withTelemetry({ method: 'patch', url, data, ...options }),
  delete: (url, data, options) =>
    withTelemetry({ method: 'delete', url, data, ...options }),
};

export default http;
